/**
 * Gates tracker pages on the `/tracker/capabilities` probe so the UI shows a
 * precise reason instead of a generic data-load failure. Three terminal states:
 *
 *   !licensed              → "not included in your license"  (gate 1, entitlement)
 *   licensed && !configured → "requires configuration"        (gate 2, operability)
 *   licensed && configured  → render the page                 (ready)
 *
 * The nav already hides tracker entries when unlicensed; this also covers
 * direct-URL access and the licensed-but-unconfigured case.
 */

import type { ReactNode } from 'react';
import { Loader2, Plug } from 'lucide-react';
import { Card, CardContent } from '@lean-agility/design-system/react';

import { useTrackerCapabilities } from '../hooks';

function GateMessage({ children, muted = true }: { children: ReactNode; muted?: boolean }) {
  return (
    <div className="container mx-auto px-4 py-8">
      <Card>
        <CardContent className="flex items-center gap-3 py-8">
          <Plug className="size-5 shrink-0 text-text-muted" />
          <p className={muted ? 'text-sm text-text-muted' : 'text-sm text-feedback-error'}>{children}</p>
        </CardContent>
      </Card>
    </div>
  );
}

export function TrackerCapabilityGate({ children }: { children: ReactNode }) {
  const { data: capabilities, isLoading, error } = useTrackerCapabilities();

  if (isLoading) {
    return (
      <div className="container mx-auto flex items-center gap-2 px-4 py-8 text-sm text-text-muted">
        <Loader2 className="size-4 animate-spin" /> Checking tracker availability…
      </div>
    );
  }

  if (error) {
    return <GateMessage muted={false}>Failed to load tracker availability. Please refresh.</GateMessage>;
  }

  if (!capabilities?.licensed) {
    return <GateMessage>Tracker integration isn&apos;t included in your license.</GateMessage>;
  }

  if (!capabilities.configured) {
    return (
      <GateMessage>
        Tracker integration requires configuration. Ask an administrator to set the <code>SHIPCAST_SECRETS_KEY</code>{' '}
        environment variable.
      </GateMessage>
    );
  }

  return <>{children}</>;
}
